import type * as React from "react";

import { cn } from "./cn";

type CheckSize = 14 | 16 | 18 | 20 | 24;

const SIZE_CLASS: Record<CheckSize, string> = {
  14: "size-3.5",
  16: "size-4",
  18: "size-4.5",
  20: "size-5",
  24: "size-6",
};

// `inline` centres the disc on the first line of the text beside it.
const INLINE_CLASS: Record<CheckSize, string> = {
  14: "mt-[calc((1lh-14px)/2)]",
  16: "mt-[calc((1lh-16px)/2)]",
  18: "mt-[calc((1lh-18px)/2)]",
  20: "mt-[calc((1lh-20px)/2)]",
  24: "mt-[calc((1lh-24px)/2)]",
};

type CheckProps = Omit<React.ComponentProps<"span">, "children"> & {
  /** Disc diameter in px. 20 is the default; 14 or 16 fits inside a chip. */
  size?: CheckSize;
  /** White disc, forest tick: the same mark on a forest surface. */
  inverse?: boolean;
  /** Sit on the first line of wrapping text beside it. */
  inline?: boolean;
  /** Accessible name, only when the mark stands alone and must be announced. */
  label?: string;
};

/**
 * The one check mark (Check.astro): a solid forest disc with a white tick.
 * Decorative by default — the text beside it says what is done.
 */
function Check({
  size = 20,
  inverse = false,
  inline = false,
  label,
  className,
  ...props
}: CheckProps) {
  return (
    <span
      data-slot="check"
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      aria-label={label}
      className={cn(
        "inline-grid shrink-0 place-items-center rounded-pill",
        inverse ? "bg-card text-primary" : "bg-primary text-primary-foreground",
        SIZE_CLASS[size],
        inline && INLINE_CLASS[size],
        className,
      )}
      {...props}
    >
      <svg
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        className="size-[62%]"
      >
        <path
          d="M2.6 6.3 5 8.6l4.4-5"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export { Check };
export type { CheckProps, CheckSize };
